// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useEffect } from 'react';
import { brokerageConfigApi, type BrokeragePolicyType } from '../api/brokerageConfigApi';

const CATEGORY_LABELS: Record<string, string> = {
  PENSION: 'Pension',
  INSURANCE: 'Insurance',
  INVESTMENT: 'Investment',
  BANKING: 'Banking',
};

const DEFAULT_OPTIONS = [{ text: 'Pension', value: 'PENSION' }];

export interface PolicyTypeOption {
  text: string;
  value: string;
}

export function usePolicyTypeOptions(brokerageId: string, providerId: string) {
  const [categoryOptions, setCategoryOptions] = useState<PolicyTypeOption[]>(DEFAULT_OPTIONS);
  const [policyTypes, setPolicyTypes] = useState<BrokeragePolicyType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brokerageId || !providerId) {
      setCategoryOptions(DEFAULT_OPTIONS);
      setPolicyTypes([]);
      return;
    }

    let cancelled = false;

    const loadPolicyTypes = async () => {
      setLoading(true);
      try {
        const response = await brokerageConfigApi.getEnabledPolicyTypes(brokerageId, providerId);

        if (cancelled) return;

        if (response.content.length === 0) {
          setCategoryOptions(DEFAULT_OPTIONS);
          setPolicyTypes([]);
          return;
        }

        setPolicyTypes(response.content);

        // Extract unique categories
        const categories = new Set<string>();
        for (const bpt of response.content) {
          if (bpt.policyType?.category) {
            categories.add(bpt.policyType.category);
          }
        }

        if (categories.size === 0) {
          setCategoryOptions(DEFAULT_OPTIONS);
          return;
        }

        const options: PolicyTypeOption[] = Array.from(categories).map(cat => ({
          text: CATEGORY_LABELS[cat] || cat,
          value: cat,
        }));

        setCategoryOptions(options);
      } catch {
        if (!cancelled) {
          setCategoryOptions(DEFAULT_OPTIONS);
          setPolicyTypes([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPolicyTypes();

    return () => {
      cancelled = true;
    };
  }, [brokerageId, providerId]);

  return { categoryOptions, policyTypes, loading };
}
